import { ref, computed, readonly, type DeepReadonly, type Ref } from "vue";
import type Keycloak from "keycloak-js";
import { getKeycloak } from "./keycloak";
import { clearStoredTenantConfig } from "../tenant/resolveTenantConfig";
import { consumeLoginHint } from "../tenant/loginHint";
import type {
  AuthConfig,
  AuthState,
  Perfil,
  UsuarioMe,
  MeCompany,
} from "./types";
import type { Permission } from "../access/types";

// ── Estado reativo singleton (compartilhado entre todos os componentes) ──

const initialized = ref(false);
const authenticated = ref(false);
const permissionsLoaded = ref(false);
const perfis = ref<Perfil[]>([]);
const capabilities = ref<Set<Permission>>(new Set());
const capabilitiesProvidedByApi = ref(false);
const companyIdFromApi = ref<string | null>(null);
const primeiroAcesso = ref(false);

let kc: Keycloak | null = null;
let initPromise: Promise<boolean> | null = null;
let httpGet: (<T>(url: string) => Promise<{ data: T }>) | null = null;

// ── Helpers internos ──

function toPermissionSet(raw: readonly Permission[] | undefined): Set<Permission> {
  return new Set(Array.isArray(raw) ? raw : []);
}

function clearStaleLoginErrorFragment(): void {
  if (!window.location.hash.includes("error=login_required")) return;
  window.history.replaceState(
    window.history.state,
    document.title,
    `${window.location.pathname}${window.location.search}`,
  );
}

async function loadUserProfile(): Promise<void> {
  if (!httpGet) return;
  try {
    const meResp = await httpGet<MeCompany>("/api/me");
    companyIdFromApi.value = meResp.data.companyId ?? null;

    if (Array.isArray(meResp.data.capabilities)) {
      capabilities.value = toPermissionSet(meResp.data.capabilities);
      capabilitiesProvidedByApi.value = true;
    } else {
      capabilities.value = new Set();
      capabilitiesProvidedByApi.value = false;
    }
  } catch {
    companyIdFromApi.value = null;
    capabilities.value = new Set();
    capabilitiesProvidedByApi.value = false;
  }

  try {
    const usuarioResp = await httpGet<UsuarioMe>("/api/usuarios/me");
    perfis.value = usuarioResp.data.perfis ?? [];
    primeiroAcesso.value = usuarioResp.data.primeiroAcesso ?? false;
  } catch {
    perfis.value = [];
    primeiroAcesso.value = false;
  } finally {
    permissionsLoaded.value = true;
  }
}

function clearState(): void {
  authenticated.value = false;
  permissionsLoaded.value = false;
  perfis.value = [];
  capabilities.value = new Set();
  capabilitiesProvidedByApi.value = false;
  companyIdFromApi.value = null;
  primeiroAcesso.value = false;
}

function clearLoginContext(): void {
  clearState();
  clearStoredTenantConfig();
}

// ── API publica ──

export interface UseAuthReturn {
  /** true apos keycloak.init() resolver. */
  initialized: DeepReadonly<Ref<boolean>>;
  /** true apos /api/me e /api/usuarios/me retornarem ou falharem. */
  permissionsLoaded: DeepReadonly<Ref<boolean>>;
  /** true se o callback de login autenticou o usuário. */
  authenticated: DeepReadonly<Ref<boolean>>;
  /** true se usuario esta no primeiro acesso. */
  primeiroAcesso: DeepReadonly<Ref<boolean>>;
  /** Token JWT atual ou null. */
  token: Readonly<Ref<string | null>>;
  /** Company ID (da API ou fallback do JWT). */
  companyId: Readonly<Ref<string | null>>;
  /** Company name (do JWT). */
  companyName: Readonly<Ref<string | null>>;
  /** Email do usuario autenticado. */
  email: Readonly<Ref<string>>;
  /** Nome completo do usuario (do JWT). */
  fullName: Readonly<Ref<string | null>>;
  /** Slug do realm Keycloak. */
  realmSlug: Readonly<Ref<string>>;
  /** Roles do realm Keycloak (realm_access.roles do JWT) — papéis de plataforma (SUPER_ADMIN, …). */
  realmRoles: Readonly<Ref<string[]>>;
  /** Perfis carregados via /api/usuarios/me, mantidos para exibicao e badges. */
  perfis: DeepReadonly<Ref<Perfil[]>>;
  /** Capacidades de UI resolvidas pelo backend a partir das authorities do JWT. */
  capabilities: DeepReadonly<Ref<Set<Permission>>>;
  /** true quando /api/me trouxe explicitamente o campo capabilities. */
  capabilitiesProvidedByApi: DeepReadonly<Ref<boolean>>;
  /** AuthState agregado (ou null se nao autenticado). */
  auth: Readonly<Ref<AuthState | null>>;
  /** Verifica se o usuario tem PELO MENOS UM dos perfis informados. */
  temPerfil: (...required: Perfil[]) => boolean;
  /** Verifica se o usuario tem PELO MENOS UMA das roles de realm informadas (ex.: SUPER_ADMIN). */
  hasRealmRole: (...required: string[]) => boolean;
  /** Inicia fluxo de login Keycloak, sempre exigindo senha. */
  login: () => Promise<void>;
  /** Encerra sessao. */
  logout: () => void;
  /** Recarrega perfis e capabilities do usuario (ex.: apos alterar a matriz de acesso). */
  reloadPermissions: () => Promise<void>;
  /** Inicializa o modulo (chamar uma vez no bootstrap do app). */
  init: (
    config: AuthConfig,
    apiGet: <T>(url: string) => Promise<{ data: T }>,
  ) => Promise<void>;
}

/**
 * Composable central de autenticacao.
 *
 * Não executa `check-sso`: cookies de sessões anteriores do Keycloak nunca
 * autenticam o usuário silenciosamente. O único caminho para obter um token é
 * o callback de um login interativo iniciado com `prompt=login` e `maxAge=0`.
 */
export function useAuth(): UseAuthReturn {
  // ── Computed derivados ──

  const token = computed<string | null>(() =>
    authenticated.value ? (kc?.token ?? null) : null,
  );

  const companyId = computed<string | null>(() => {
    if (!authenticated.value) return null;
    if (companyIdFromApi.value) return companyIdFromApi.value;
    const parsed = kc?.tokenParsed as Record<string, unknown> | undefined;
    return (
      (parsed?.company_id as string | undefined) ??
      (parsed?.companyId as string | undefined) ??
      null
    );
  });

  const companyName = computed<string | null>(() => {
    if (!authenticated.value) return null;
    const parsed = kc?.tokenParsed as Record<string, unknown> | undefined;
    return (
      (parsed?.company_name as string | undefined) ??
      (parsed?.companyName as string | undefined) ??
      null
    );
  });

  const email = computed<string>(() => {
    if (!authenticated.value) return "";
    const parsed = kc?.tokenParsed;
    return (
      parsed?.email ??
      (parsed?.preferred_username as string | undefined) ??
      ""
    );
  });

  const fullName = computed<string | null>(() => {
    if (!authenticated.value) return null;
    const parsed = kc?.tokenParsed as Record<string, unknown> | undefined;
    const fromName = parsed?.name as string | undefined;
    if (fromName) return fromName;
    const combined = [
      parsed?.given_name as string | undefined,
      parsed?.family_name as string | undefined,
    ]
      .filter(Boolean)
      .join(" ");
    return combined || null;
  });

  const realmSlug = computed<string>(() =>
    kc?.realm ?? "inbim",
  );

  /**
   * Roles do realm Keycloak ({@code realm_access.roles} do JWT) — a mesma fonte que o backend usa
   * para papéis de plataforma como {@code SUPER_ADMIN}/{@code TENANT_ADMIN} (ver
   * {@code KeycloakJwtAuthenticationConverter}). Distinto de {@code perfis}, que vem de
   * {@code /api/usuarios/me} e cobre papéis de aplicação (ADMIN, GESTOR, …), não de plataforma.
   */
  const realmRoles = computed<string[]>(() => {
    if (!authenticated.value) return [];
    const parsed = kc?.tokenParsed as { realm_access?: { roles?: unknown } } | undefined;
    const roles = parsed?.realm_access?.roles;
    return Array.isArray(roles) ? (roles as string[]) : [];
  });

  const auth = computed<AuthState | null>(() => {
    if (!authenticated.value) return null;
    return {
      accessToken: kc?.token ?? "",
      exp: kc?.tokenParsed?.exp ?? null,
      companyId: companyId.value,
      companyName: companyName.value,
      email: email.value,
      perfis: perfis.value,
      capabilities: Array.from(capabilities.value),
    };
  });

  // ── Metodos ──

  function temPerfil(...required: Perfil[]): boolean {
    return perfis.value.some((p) => required.includes(p));
  }

  function hasRealmRole(...required: string[]): boolean {
    return realmRoles.value.some((r) => required.includes(r));
  }

  async function login(): Promise<void> {
    const loginHint = consumeLoginHint();
    await kc?.login({
      prompt: "login",
      maxAge: 0,
      ...(loginHint ? { loginHint } : {}),
    });
  }

  function logout(): void {
    clearLoginContext();
    kc?.logout({ redirectUri: window.location.origin });
  }

  /**
   * Inicializa Keycloak somente para processar um eventual callback OAuth.
   * Sem callback, permanece não autenticado e o router inicia login explícito.
   */
  async function init(
    config: AuthConfig,
    apiGet: <T>(url: string) => Promise<{ data: T }>,
  ): Promise<void> {
    httpGet = apiGet;
    clearStaleLoginErrorFragment();
    kc = getKeycloak(config);

    if (!initPromise) {
      const KC_INIT_TIMEOUT_MS = 10_000;
      const kcInit = kc.init({
        checkLoginIframe: false,
        pkceMethod: "S256",
      });
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Keycloak init timeout")), KC_INIT_TIMEOUT_MS),
      );
      initPromise = Promise.race([kcInit, timeout]);
    }

    try {
      const isAuth = await initPromise;
      authenticated.value = isAuth;
      if (isAuth) {
        await loadUserProfile();
      }
      initialized.value = true;
    } catch {
      initialized.value = true;
    }

    kc.onTokenExpired = () => {
      kc!.updateToken(60).catch(() => {
        clearLoginContext();
        kc!.logout({ redirectUri: window.location.origin });
      });
    };

    kc.onAuthRefreshSuccess = () => {
      authenticated.value = true;
    };

    kc.onAuthLogout = () => {
      clearLoginContext();
    };

    kc.onAuthSuccess = () => {
      authenticated.value = true;
      loadUserProfile();
    };
  }

  return {
    initialized: readonly(initialized),
    permissionsLoaded: readonly(permissionsLoaded),
    authenticated: readonly(authenticated),
    primeiroAcesso: readonly(primeiroAcesso),
    token,
    companyId,
    companyName,
    email,
    fullName,
    realmSlug,
    realmRoles,
    perfis: readonly(perfis),
    capabilities: readonly(capabilities),
    capabilitiesProvidedByApi: readonly(capabilitiesProvidedByApi),
    auth,
    temPerfil,
    hasRealmRole,
    login,
    logout,
    reloadPermissions: loadUserProfile,
    init,
  };
}

/**
 * Acesso direto ao Keycloak (para interceptors HTTP).
 * Nao usar em componentes — preferir useAuth().
 */
export function getKeycloakInstance(): Keycloak | null {
  return kc;
}
